import React from "react";
import { Icon, EmptyState, Portal, woBOM, poOnOrder, poFromShortages, PurchaseOrderModal, notify, confirmDialog, DataTable } from "./registry";
/* ============================================================
   Fence Works — Inventory (full management, Phase 1)
   Real Supabase tables (supabase_schema_inventory.sql): tracked items
   (catalog-linked or custom), a stock LEDGER (every receive / consume /
   adjust / count / return with who+why+cost snapshot), suppliers, and
   the demand feed straight from open jobs' estimates via engine_label.
   Crews log material used on a job in two taps (extra rail, damaged
   picket…) — it hits the ledger with the job as the reference.
   No cloud / schema not applied → the legacy settings-count table
   still works, with a callout showing how to unlock the full build.
   ============================================================ */
const IV_money0 = window.VFP.money0;
// the concrete service's items ride the same shelf: foam sets, coating kits, sealant tubes (§13 item 18)
const CATEGORIES = ["post", "panel", "picket", "rail", "hardware", "concrete", "gate", "cap", "foam", "coating", "sealant", "other"];
const UNITS = ["ea", "bag", "lf", "box", "roll", "set", "kit", "lb", "gal", "tube"];

function NumCell({ value, onCommit, width = 64 }: any) {
  const [draft, setDraft] = React.useState<any>(value == null ? "" : String(value));
  React.useEffect(() => { setDraft(value == null ? "" : String(value)); }, [value]);
  const commit = () => { const n = parseFloat(draft); onCommit(isFinite(n) ? Math.max(0, Math.round(n)) : null); };
  return (
    <span className="rep-spend">
      <input className="tnum" type="number" min="0" style={{ width }} value={draft} placeholder="—"
        onChange={e => setDraft(e.target.value)} onBlur={commit}
        onKeyDown={e => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }} />
    </span>
  );
}

/* demand per BOM label across every unfinished job — the same engine rows the
   estimates, crew sheets and POs use, so they can never drift */
function demandByLabel(store: any) {
  const by: any = {};
  (store.workOrders || []).forEach((wo: any) => {
    if (wo.status === "completed") return;
    (woBOM(wo, store) || []).forEach((b: any) => {
      const r = by[b.label] || (by[b.label] = { label: b.label, needed: 0, jobs: 0 });
      r.needed += Number(b.qty) || 0; r.jobs++;
    });
  });
  return by;
}

const fmtAt = (t: any) => { const d = new Date(t); return isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) + " " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }); };
const KIND_META: any = {
  receive: { label: "Received", color: "var(--green)" },
  consume: { label: "Used", color: "var(--amber)" },
  return:  { label: "Returned", color: "var(--teal)" },
  adjust:  { label: "Adjusted", color: "var(--fg-3)" },
  count:   { label: "Counted", color: "var(--blue)" },
};

/* ---- crew-facing "log material used" — also mounted on work orders & Crew
   Mode. Two taps: item, qty, reason → ledger row referencing the job. ---- */
function LogMaterialUse({ wo, store, onDone }: any) {
  const [open, setOpen] = React.useState<any>(false);
  const [items, setItems] = React.useState<any>(null);
  const [itemName, setItemName] = React.useState<any>("");
  const [qty, setQty] = React.useState<any>("1");
  const [reason, setReason] = React.useState<any>("extra");
  const [busy, setBusy] = React.useState<any>(false);
  const [msg, setMsg] = React.useState<any>("");
  React.useEffect(() => {
    if (!open || items) return;
    const ST: any = window.VFP_STOCK;
    if (ST && ST.enabled()) ST.items().then((rows: any) => setItems(rows || [])).catch(() => setItems([]));
    else setItems([]);
  }, [open]);
  const REASONS: any = [["extra", "Used extra"], ["damaged", "Damaged"], ["returned", "Returned to yard"]];
  async function log() {
    const n = Math.max(0, Math.round(+qty || 0));
    const item = (items || []).find((x: any) => x.name === itemName);
    if (!item || !n) { setMsg(item ? "Enter a quantity" : "Pick an item from the list"); return; }
    setBusy(true); setMsg("");
    try {
      const ST: any = window.VFP_STOCK;
      const kind = reason === "returned" ? "return" : "consume";
      const label = (REASONS.find((r: any) => r[0] === reason) || [])[1] || "";
      await ST.move(item, kind === "return" ? n : -n, kind, { ref: wo ? "wo:" + wo.id : null, note: label + (wo && wo.customer ? " · " + wo.customer : "") });
      if (store && store.addActivity) store.addActivity(`${kind === "return" ? "Returned" : "Used"} ${n} × ${item.name}${wo && wo.customer ? " on " + wo.customer : ""}`);
      setMsg("✓ Logged"); setQty("1");
      setTimeout(() => { setOpen(false); setMsg(""); onDone && onDone(); }, 900);
    } catch (e: any) { setMsg(String((e && e.message) || e).slice(0, 80)); }
    finally { setBusy(false); }
  }
  return (
    <div style={{ marginTop: 10 }}>
      {!open
        ? <button className="btn btn-ghost btn-sm" onClick={() => setOpen(true)} title="Grabbed an extra rail? Broke a picket? Log it against this job — inventory and the ledger update">
            <Icon name="box" size={13} />Log material used
          </button>
        : (
          <div style={{ border: "1px solid var(--line)", borderRadius: 10, padding: "10px 12px", background: "var(--surface-2)", display: "grid", gap: 8 }}>
            <div className="row between">
              <span className="kicker">Material used{wo && wo.customer ? " · " + wo.customer : ""}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setOpen(false)}><Icon name="x" size={12} /></button>
            </div>
            {items === null ? <span className="muted" style={{ fontSize: 12 }}>Loading items…</span> : items.length === 0 ? (
              <span className="muted" style={{ fontSize: 12 }}>No tracked items yet — open Inventory and add or import them first.</span>
            ) : (
              <>
                <div className="row gap-8" style={{ flexWrap: "wrap" }}>
                  <input className="input" list={"inv-items-" + (wo ? wo.id : "x")} style={{ flex: "1 1 180px" }} placeholder="Item…" value={itemName} onChange={e => setItemName(e.target.value)} />
                  <datalist id={"inv-items-" + (wo ? wo.id : "x")}>{items.map((x: any) => <option key={x.id} value={x.name} />)}</datalist>
                  <input className="input tnum" type="number" min="1" style={{ width: 76 }} value={qty} onChange={e => setQty(e.target.value)} />
                </div>
                <div className="row gap-8" style={{ flexWrap: "wrap" }}>
                  {REASONS.map(([id, lb]: any) => <button key={id} className={"filter-chip" + (reason === id ? " on" : "")} onClick={() => setReason(id)}>{lb}</button>)}
                </div>
                <div className="row gap-8">
                  <button className="btn btn-primary btn-sm" disabled={busy} onClick={log}><Icon name="check" size={13} />{busy ? "Logging…" : "Log it"}</button>
                  {msg && <span className="muted" style={{ fontSize: 12 }}>{msg}</span>}
                </div>
              </>
            )}
          </div>
        )}
    </div>
  );
}

/* ---- item editor (add custom / edit any) ---- */
function ItemModal({ initial, suppliers, onSave, onDelete, onClose }: any) {
  const [it, setIt] = React.useState<any>(initial);
  const set = (patch: any) => setIt((x: any) => ({ ...x, ...patch }));
  return (
    <Portal>
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" style={{ width: "min(540px, 96vw)" }} onClick={(e: any) => e.stopPropagation()}>
        <h3 className="display" style={{ fontSize: 18, margin: "0 0 12px" }}>{it.id ? "Edit item" : "New item"}</h3>
        <div style={{ display: "grid", gap: 10 }}>
          <div className="row gap-8" style={{ flexWrap: "wrap" }}>
            <div style={{ flex: "2 1 200px" }}><label className="field-label">Name</label>
              <input className="input" value={it.name || ""} placeholder="6' Vinyl Privacy Panel — White" onChange={e => set({ name: e.target.value })} /></div>
            <div style={{ flex: "1 1 130px" }}><label className="field-label">Brand</label>
              <input className="input" value={it.brand || ""} placeholder="Bufftech, Master Halco…" onChange={e => set({ brand: e.target.value })} /></div>
            <div style={{ flex: "1 1 110px" }}><label className="field-label">SKU</label>
              <input className="input" value={it.sku || ""} placeholder="Supplier SKU" onChange={e => set({ sku: e.target.value })} /></div>
          </div>
          <div className="row gap-8" style={{ flexWrap: "wrap" }}>
            <div><label className="field-label">Category</label>
              <select className="select" value={it.category || "other"} onChange={e => set({ category: e.target.value })}>{CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
            <div><label className="field-label">Unit</label>
              <select className="select" value={it.unit || "ea"} onChange={e => set({ unit: e.target.value })}>{UNITS.map(u => <option key={u} value={u}>{u}</option>)}</select></div>
            <div><label className="field-label">Unit cost $</label>
              <input className="input tnum" type="number" min="0" step="0.01" style={{ width: 110 }} value={it.cost == null ? "" : it.cost} onChange={e => set({ cost: e.target.value === "" ? null : +e.target.value })} /></div>
            <div><label className="field-label">Reorder at</label>
              <input className="input tnum" type="number" min="0" style={{ width: 90 }} value={it.reorder_at == null ? "" : it.reorder_at} onChange={e => set({ reorder_at: e.target.value === "" ? null : Math.max(0, Math.round(+e.target.value)) })} /></div>
          </div>
          <div className="row gap-8" style={{ flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 200px" }}><label className="field-label">Estimator BOM label — links demand from jobs</label>
              <input className="input" value={it.engine_label || ""} placeholder='e.g. "Line Post" (exactly as estimates show it)' onChange={e => set({ engine_label: e.target.value })} /></div>
            <div style={{ flex: "1 1 140px" }}><label className="field-label">Location / bin</label>
              <input className="input" value={it.location || ""} placeholder="Rack B · Trailer 2" onChange={e => set({ location: e.target.value })} /></div>
            <div style={{ flex: "1 1 150px" }}><label className="field-label">Supplier</label>
              <select className="select" value={it.supplier_id || ""} onChange={e => set({ supplier_id: e.target.value || null })}>
                <option value="">—</option>
                {(suppliers || []).map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select></div>
          </div>
        </div>
        <div className="row gap-8" style={{ marginTop: 14, flexWrap: "wrap" }}>
          <button className="btn btn-primary btn-sm" onClick={() => { if (!(it.name || "").trim()) return; onSave({ ...it, custom: it.custom !== false && !it.catalog_item_id }); }}><Icon name="check" size={13} />Save item</button>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          {it.id && <button className="btn btn-ghost btn-sm" style={{ marginLeft: "auto", color: "var(--red)" }} onClick={async () => { if (await confirmDialog({ title: "Delete this item?", body: "It comes off the stock list; its ledger history stays.", confirmLabel: "Delete item", danger: true })) onDelete(it.id); }}><Icon name="trash" size={13} />Delete</button>}
        </div>
      </div>
    </div>
    </Portal>
  );
}

/* ---- adjust-stock modal: reasons, job link, count mode ---- */
function AdjustModal({ item, store, onDone, onClose }: any) {
  const [mode, setMode] = React.useState<any>("adjust");   // adjust | count
  const [qty, setQty] = React.useState<any>("");
  const [reason, setReason] = React.useState<any>("used");
  const [woId, setWoId] = React.useState<any>("");
  const [busy, setBusy] = React.useState<any>(false);
  const [err, setErr] = React.useState<any>("");
  const openJobs = (store.workOrders || []).filter((w: any) => w.status !== "completed");
  const REASONS: any = [["used", "Used on a job"], ["damaged", "Damaged / scrap"], ["found", "Found / correction"], ["returned", "Returned to stock"], ["sold", "Sold direct"]];
  const inbound = reason === "found" || reason === "returned";
  async function apply() {
    const ST: any = window.VFP_STOCK;
    const n = Math.max(0, Math.round(+qty || 0));
    if (mode === "adjust" && !n) { setErr("Enter a quantity"); return; }
    setBusy(true); setErr("");
    try {
      if (mode === "count") await ST.setCount(item, Math.max(0, Math.round(+qty || 0)), "cycle count");
      else {
        const kind = reason === "used" ? "consume" : reason === "returned" ? "return" : "adjust";
        const wo = openJobs.find((w: any) => w.id === woId);
        await ST.move(item, inbound ? n : -n, kind, {
          ref: wo ? "wo:" + wo.id : null,
          note: ((REASONS.find((r: any) => r[0] === reason) || [])[1] || "") + (wo ? " · " + (wo.customer || "job") : ""),
        });
        if (wo && store.addActivity) store.addActivity(`Inventory: ${inbound ? "+" : "−"}${n} × ${item.name} (${wo.customer || "job"})`);
      }
      onDone();
    } catch (e: any) { setErr(String((e && e.message) || e).slice(0, 90)); setBusy(false); }
  }
  return (
    <Portal>
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" style={{ width: "min(480px, 96vw)" }} onClick={(e: any) => e.stopPropagation()}>
        <h3 className="display" style={{ fontSize: 18, margin: "0 0 4px" }}>{item.name}</h3>
        <p className="muted" style={{ fontSize: 12.5, margin: "0 0 12px" }}>On hand: <b className="tnum">{Number(item.on_hand) || 0}</b>{item.location ? " · " + item.location : ""}</p>
        <div className="row gap-8" style={{ marginBottom: 10 }}>
          <button className={"filter-chip" + (mode === "adjust" ? " on" : "")} onClick={() => setMode("adjust")}>Adjust</button>
          <button className={"filter-chip" + (mode === "count" ? " on" : "")} onClick={() => setMode("count")}>Set count</button>
        </div>
        {mode === "adjust" ? (
          <div style={{ display: "grid", gap: 10 }}>
            <div className="row gap-8" style={{ flexWrap: "wrap" }}>
              {REASONS.map(([id, lb]: any) => <button key={id} className={"filter-chip" + (reason === id ? " on" : "")} onClick={() => setReason(id)}>{lb}</button>)}
            </div>
            <div className="row gap-8" style={{ alignItems: "center" }}>
              <span className="display tnum" style={{ fontSize: 18, color: inbound ? "var(--green)" : "var(--red)" }}>{inbound ? "+" : "−"}</span>
              <input className="input tnum" type="number" min="0" autoFocus style={{ width: 110 }} value={qty} onChange={e => setQty(e.target.value)} />
              <span className="muted" style={{ fontSize: 13 }}>{item.unit || "ea"}</span>
            </div>
            {reason === "used" && (
              <div><label className="field-label">On which job? (optional — links the ledger entry)</label>
                <select className="select" value={woId} onChange={e => setWoId(e.target.value)}>
                  <option value="">No specific job</option>
                  {openJobs.map((w: any) => <option key={w.id} value={w.id}>{w.customer || w.title || w.id}</option>)}
                </select></div>
            )}
          </div>
        ) : (
          <div className="row gap-8" style={{ alignItems: "center" }}>
            <span className="muted" style={{ fontSize: 13 }}>Counted:</span>
            <input className="input tnum" type="number" min="0" autoFocus style={{ width: 110 }} value={qty} onChange={e => setQty(e.target.value)} />
            <span className="muted" style={{ fontSize: 12.5 }}>the delta lands on the ledger as a count</span>
          </div>
        )}
        {err && <p style={{ color: "var(--red)", fontSize: 12.5, margin: "10px 0 0" }}>{err}</p>}
        <div className="row gap-8" style={{ marginTop: 14 }}>
          <button className="btn btn-primary btn-sm" disabled={busy} onClick={apply}><Icon name="check" size={13} />{busy ? "Saving…" : "Apply"}</button>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
    </Portal>
  );
}

function InventoryPage({ store, go }: any) {
  const settings = store.settings || {};
  const legacyInv = settings.inventory || {};
  const [stockMode, setStockMode] = React.useState<any>("loading");   // loading | ready | no-schema | off | error
  const [stockErr, setStockErr] = React.useState<any>("");            // why the cloud read failed (mode "error")
  const [items, setItems] = React.useState<any>([]);
  const [movesL, setMovesL] = React.useState<any>([]);
  const [sups, setSups] = React.useState<any>([]);
  const [tab, setTab] = React.useState<any>("stock");
  const [editItem, setEditItem] = React.useState<any>(null);
  const [adjItem, setAdjItem] = React.useState<any>(null);
  const [poModal, setPoModal] = React.useState<any>(null);
  const [note, setNote] = React.useState<any>("");
  const [loc, setLoc] = React.useState<any>("all");   // where: the yard, Truck 1… (the item's location; "foam sets on the truck", §13 item 18)
  const onOrder = poOnOrder(settings);
  const demand = React.useMemo(() => demandByLabel(store), [store.workOrders, store.quotes, store.projects, store.settings]);
  const demandRef = React.useRef(demand); demandRef.current = demand;

  const reload = React.useCallback(async () => {
    const ST: any = window.VFP_STOCK;
    if (!(ST && ST.enabled())) { setStockMode("off"); return; }
    try {
      const st = await ST.status();
      if (st !== "ready") { setStockMode(st === "no-schema" ? "no-schema" : "off"); return; }
      // one-time migration: legacy counts + current demand labels → tracked items
      const migrated = await ST.migrateLegacy((store.settings || {}).inventory || {}, Object.keys(demandRef.current));
      // items(true) = deleted ones too, so ledger rows keep their item names
      const [rows, mv, sp] = await Promise.all([ST.items(true), ST.moves(60), ST.suppliers()]);
      setItems(rows || []); setMovesL(mv || []); setSups(sp || []);
      setStockMode("ready");
      if (migrated) setNote(`Migrated ${migrated} items from the old counts`);
    } catch (e: any) {
      // a failed cloud read is NOT "inventory is off" — say so and offer a retry
      // instead of silently showing the legacy local counts as if that's all there is
      setStockErr(String((e && e.message) || e || "unknown error").slice(0, 160));
      setStockMode("error");
    }
  }, []);
  React.useEffect(() => { reload(); }, []);
  // supplier field edits save on blur — a failed save must say so (the input
  // still shows the typed value, so silence would read as "saved")
  async function saveSupplierField(row: any) {
    const ST: any = window.VFP_STOCK;
    try { await ST.saveSupplier(row); }
    catch (e: any) { notify("Couldn't save the supplier — " + String((e && e.message) || e).slice(0, 140)); }
  }

  // rows: every ACTIVE tracked item; demand matches by engine_label, then name
  // (deleted items stay in `items` only so the activity ledger can name them)
  const activeItems = items.filter((it: any) => it.active !== false);
  const itemByLabel: any = {};
  activeItems.forEach((it: any) => { if (it.engine_label && !itemByLabel[it.engine_label]) itemByLabel[it.engine_label] = it; if (!itemByLabel[it.name]) itemByLabel[it.name] = it; });
  const untracked = Object.keys(demand).filter(l => !itemByLabel[l]);
  const rows = activeItems.map((it: any) => {
    const d = demand[it.engine_label] || demand[it.name] || { needed: 0, jobs: 0 };
    const ordered = onOrder[it.engine_label] || onOrder[it.name] || 0;
    const onHand = Number(it.on_hand) || 0;
    const short = Math.max(0, d.needed - onHand);
    const stillShort = Math.max(0, short - ordered);
    const low = it.reorder_at != null && onHand <= Number(it.reorder_at);
    return { it, d, ordered, onHand, short, stillShort, low, value: it.cost != null ? onHand * Number(it.cost) : null };
  });
  const locations: string[] = (Array.from(new Set(rows.map(r => String(r.it.location || "").trim()).filter(Boolean))) as string[]).sort();
  const shown = loc === "all" ? rows : rows.filter(r => String(r.it.location || "").trim() === loc);
  const uncovered = rows.filter(r => r.stillShort > 0);
  const lows = rows.filter(r => r.low && !r.stillShort);
  const totalValue = rows.reduce((a, r) => a + (r.value || 0), 0);
  const anyCost = rows.some(r => r.value != null);

  async function delItem(it: any) {
    if (!(await confirmDialog({ title: 'Delete "' + it.name + '"?', body: "It comes off the stock list; its ledger history stays.", confirmLabel: "Delete item", danger: true }))) return;
    const ST: any = window.VFP_STOCK;
    try { await ST.deleteItem(it.id); } catch (e: any) { notify("Couldn't delete — " + String((e && e.message) || e).slice(0, 120)); }
    reload();
  }

  async function importUntracked() {
    const ST: any = window.VFP_STOCK;
    try {
      for (const label of untracked) {
        const unit = /lb set|foam/i.test(label) ? "set" : /kit/i.test(label) ? "kit" : /gal/i.test(label) ? "gal" : /tube|cartridge/i.test(label) ? "tube" : /concrete|bag/i.test(label) ? "bag" : "ea";
        const category = /foam/i.test(label) ? "foam" : /coat|flake|primer|epoxy|polyaspartic/i.test(label) ? "coating" : /sealant|caulk|filler/i.test(label) ? "sealant" : undefined;
        await ST.saveItem({ name: label, engine_label: label, unit, ...(category ? { category } : {}), on_hand: 0, custom: false, active: true });
      }
      setNote(`Added ${untracked.length} items from open estimates`);
    } catch (e) { /* partial adds surface on reload */ }
    reload();
  }

  /* ---------- legacy fallback (no cloud / schema not applied) ---------- */
  if (stockMode !== "ready") {
    const setItemL = (label: string, patch: any) => store.setSettings({ inventory: { ...legacyInv, [label]: { ...(legacyInv[label] || {}), ...patch } } });
    const labels = Array.from(new Set([...Object.keys(demand), ...Object.keys(legacyInv)])).sort();
    const lrows = labels.map(label => {
      const d = demand[label] || { needed: 0, jobs: 0 };
      const rec = legacyInv[label] || {};
      const onHand = rec.onHand != null ? +rec.onHand : null;
      const ordered = onOrder[label] || 0;
      const short = onHand != null ? Math.max(0, d.needed - onHand) : null;
      const stillShort = short != null ? Math.max(0, short - ordered) : null;
      return { label, ...d, onHand, ordered, short, stillShort };
    });
    return (
      <div className="page fade-up" style={{ maxWidth: 1000 }}>
        <div className="row between" style={{ marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
          <div><h1 className="display" style={{ fontSize: 28, margin: 0 }}>Inventory</h1>
            <p className="muted" style={{ margin: "4px 0 0" }}>{stockMode === "loading" ? "Checking cloud inventory…" : stockMode === "error" ? "Cloud inventory didn't load — showing local counts." : "Quick counts. Demand flows in from open estimates."}</p></div>
          <button className="btn btn-ghost" onClick={() => go("purchaseorders")}><Icon name="box" size={15} />Purchase Orders</button>
        </div>
        {stockMode === "error" && (
          <div className="pe-callout flat" style={{ marginBottom: 14 }}>
            <div className="row between" style={{ gap: 10, flexWrap: "wrap" }}>
              <p className="muted" style={{ margin: 0, fontSize: 12.5 }}><b style={{ color: "var(--red)" }}>Couldn't load cloud inventory</b> — {stockErr}</p>
              <button className="btn btn-outline btn-sm" onClick={() => { setStockMode("loading"); reload(); }}>Retry</button>
            </div>
          </div>
        )}
        {stockMode === "no-schema" && (
          <div className="pe-callout info" style={{ marginBottom: 14 }}>
            <p className="muted" style={{ margin: 0, fontSize: 12.5 }}><b style={{ color: "var(--fg-2)" }}>Unlock full inventory management</b> — run <code className="mono" style={{ color: "var(--teal)" }}>supabase_schema_inventory.sql</code> (repo root) in your Supabase SQL editor. You get tracked items with SKUs &amp; costs, a who-took-what ledger, crew usage logging from jobs, and supplier records — these counts migrate themselves automatically.</p>
          </div>
        )}
        <div className="card pad">
          {lrows.length === 0
            ? <EmptyState icon="box" title="No material demand yet" sub="Open jobs with estimates feed this screen automatically — win a job and its BOM lands here." />
            : <div style={{ overflowX: "auto" }}>
              <table className="rep-src-table inv-table">
                <thead><tr><th>Item</th><th className="num" title="Needed by open jobs">Needed</th><th className="num">On hand</th><th className="num">On order</th><th className="num">Short</th></tr></thead>
                <tbody>
                  {lrows.map(r => (
                    <tr key={r.label}>
                      <td style={{ fontWeight: 700 }}>{r.label}</td>
                      <td className="num tnum">{r.needed > 0 ? <>{r.needed} <span className="muted inv-jobs" style={{ fontSize: 11 }}>· {r.jobs} job{r.jobs > 1 ? "s" : ""}</span></> : "—"}</td>
                      <td className="num"><NumCell value={r.onHand} onCommit={(v: any) => setItemL(r.label, { onHand: v })} /></td>
                      <td className="num tnum" style={{ color: r.ordered ? "#f0a35e" : "var(--fg-3)" }}>{r.ordered || "—"}</td>
                      <td className="num tnum" style={{ fontWeight: 800, color: (r.stillShort || 0) > 0 ? "var(--red)" : "var(--fg-3)" }}>{r.onHand == null ? "—" : (r.short || 0) > 0 ? r.short : "0"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>}
        </div>
      </div>
    );
  }

  /* ---------- full cloud mode ---------- */
  return (
    <div className="page fade-up" style={{ maxWidth: 1100 }}>
      <div className="row between" style={{ marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
        <div><h1 className="display" style={{ fontSize: 28, margin: 0 }}>Inventory</h1>
          <p className="muted" style={{ margin: "4px 0 0" }}>Every count comes from the ledger — receive, use, adjust, count. Demand flows in from open estimates.</p></div>
        <div className="row gap-8" style={{ flexWrap: "wrap" }}>
          {uncovered.length > 0 && (
            <button className="btn btn-primary" title="One numbered PO covering every uncovered shortage" onClick={() => setPoModal(poFromShortages(uncovered.map(r => ({ label: r.it.engine_label || r.it.name, short: r.stillShort })), store))}>
              <Icon name="send" size={15} />Order shortages ({uncovered.length})
            </button>
          )}
          <button className="btn btn-outline" onClick={() => setEditItem({ name: "", unit: "ea", category: "other", custom: true, on_hand: 0 })}><Icon name="plus" size={15} />Add item</button>
          <button className="btn btn-ghost" onClick={() => go("purchaseorders")}><Icon name="box" size={15} />POs</button>
        </div>
      </div>

      <div className="row gap-8" style={{ marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        <div className="seg" role="tablist">
          {["stock", "activity", "suppliers"].map(t => (
            <button key={t} role="tab" aria-selected={tab === t} className={"seg-btn" + (tab === t ? " on" : "")} onClick={() => { setTab(t); if (t === "activity") reload(); }} style={{ textTransform: "capitalize" }}>{t}</button>
          ))}
        </div>
        <span style={{ marginLeft: "auto" }} className="row gap-8">
          {note && <span className="chip green" style={{ fontSize: 11 }}>{note}</span>}
          {anyCost && <span className="chip" style={{ fontSize: 11 }} title="On-hand × unit cost, items with a cost">Yard value ~{IV_money0(totalValue)}</span>}
          {uncovered.length > 0 && <span className="chip" style={{ fontSize: 11, color: "var(--red)", borderColor: "var(--red-tint)" }}>{uncovered.length} short</span>}
          {lows.length > 0 && <span className="chip" style={{ fontSize: 11, color: "var(--amber)", borderColor: "var(--amber-tint)" }}>{lows.length} low</span>}
        </span>
      </div>

      {tab === "stock" && (
        <div className="card pad">
          {untracked.length > 0 && (
            <div className="pe-callout info" style={{ marginBottom: 12 }}>
              <div className="row gap-8" style={{ flexWrap: "wrap", alignItems: "center" }}>
                <p className="muted" style={{ margin: 0, fontSize: 12.5, flex: "1 1 260px" }}><b style={{ color: "var(--fg-2)" }}>{untracked.length} material{untracked.length > 1 ? "s" : ""} from open estimates aren't tracked yet</b> — {untracked.slice(0, 3).join(", ")}{untracked.length > 3 ? "…" : ""}</p>
                <button className="btn btn-outline btn-sm" onClick={importUntracked}><Icon name="plus" size={13} />Track them all</button>
              </div>
            </div>
          )}
          {locations.length > 0 && (
            <div className="row gap-6" style={{ flexWrap: "wrap", marginBottom: 10 }}>
              <button className={"chip" + (loc === "all" ? " green" : "")} style={{ cursor: "pointer" }} onClick={() => setLoc("all")}>Everywhere</button>
              {locations.map(l => <button key={l} className={"chip" + (loc === l ? " green" : "")} style={{ cursor: "pointer" }} onClick={() => setLoc(l)} title={`What's at ${l}`}>{l} · {rows.filter(r => String(r.it.location || "").trim() === l).length}</button>)}
            </div>
          )}
          {rows.length === 0
            ? <EmptyState icon="box" title="No items yet" sub='Add custom items, or win a job and "Track them all" pulls its materials in automatically.' />
            : <div style={{ overflowX: "auto" }}>
              <table className="rep-src-table">
                <thead><tr><th>Item</th><th>Bin</th><th className="num">Needed</th><th className="num">On hand</th><th className="num">On order</th><th className="num">Cost</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {shown.map(({ it, d, ordered, onHand, short, stillShort, low }) => (
                    <tr key={it.id}>
                      <td style={{ fontWeight: 700, cursor: "pointer" }} onClick={() => setEditItem(it)} title="Edit item">
                        {it.name}
                        <span className="muted" style={{ fontWeight: 400, fontSize: 11 }}>{it.brand ? " · " + it.brand : ""}{it.sku ? " · " + it.sku : ""}{it.custom ? " · custom" : ""}</span>
                      </td>
                      <td className="muted" style={{ fontSize: 12 }}>{it.location || "—"}</td>
                      <td className="num tnum">{d.needed > 0 ? <>{d.needed} <span className="muted" style={{ fontSize: 11 }}>· {d.jobs} job{d.jobs > 1 ? "s" : ""}</span></> : "—"}</td>
                      <td className="num tnum" style={{ fontWeight: 800 }}>{onHand}</td>
                      <td className="num tnum" style={{ color: ordered ? "#f0a35e" : "var(--fg-3)" }}>{ordered || "—"}</td>
                      <td className="num tnum">{it.cost != null ? IV_money0(it.cost) : "—"}</td>
                      <td>{stillShort > 0 ? <span className="chip" style={{ fontSize: 10.5, color: "var(--red)", background: "var(--red-tint)", borderColor: "transparent" }}>ORDER {stillShort}</span>
                        : short > 0 ? <span className="chip" style={{ fontSize: 10.5, color: "#f0a35e", background: "rgba(240,163,94,0.14)", borderColor: "transparent" }}>ON ORDER</span>
                        : low ? <span className="chip" style={{ fontSize: 10.5, color: "var(--amber)", background: "var(--amber-tint)", borderColor: "transparent" }}>LOW</span>
                        : <span className="chip" style={{ fontSize: 10.5, color: "var(--green)", background: "var(--green-tint)", borderColor: "transparent" }}>OK</span>}</td>
                      <td className="num" style={{ whiteSpace: "nowrap" }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => setAdjItem(it)} title="Receive / use / adjust / count"><Icon name="edit" size={13} />Adjust</button>
                        <button className="btn btn-ghost btn-sm" style={{ color: "var(--red)", marginLeft: 2 }} title="Delete item — ledger history stays" onClick={() => delItem(it)}><Icon name="trash" size={13} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>}
          <p className="faint" style={{ fontSize: 11, margin: "10px 0 0" }}>Click an item to edit it · Adjust for any quantity change — everything lands on the ledger (Activity tab). Receiving a purchase order adds stock automatically; crews log usage from the job card.</p>
        </div>
      )}

      {tab === "activity" && (
        <div className="card pad">
          {movesL.length === 0
            ? <EmptyState icon="activity" title="No stock activity yet" sub="Receives, usage, adjustments and counts show here — who, what, when, and which job." />
            : <DataTable rowKey="id" dense initialSort={{ key: "at", dir: "desc" }}
                rows={movesL.map((m: any) => {
                  const it = items.find((x: any) => x.id === m.item_id);
                  const wo = m.ref && String(m.ref).startsWith("wo:") ? (store.workOrders || []).find((w: any) => w.id === String(m.ref).slice(3)) : null;
                  return { ...m, _item: it ? it.name : m.item_id, _ref: wo ? (wo.customer || "job") : (m.ref || ""), _at: new Date(m.at).getTime() || 0 };
                })}
                columns={[
                  { key: "at", label: "When", sort: (m: any) => m._at, render: (m: any) => <span className="muted" style={{ fontSize: 12 }}>{fmtAt(m.at)}</span> },
                  { key: "kind", label: "Kind", render: (m: any) => { const meta = KIND_META[m.kind] || KIND_META.adjust; return <b style={{ color: meta.color }}>{meta.label}</b>; } },
                  { key: "qty", label: "Qty", num: true, render: (m: any) => <b>{m.qty > 0 ? "+" : ""}{m.qty}</b> },
                  { key: "_item", label: "Item", render: (m: any) => <span style={{ fontWeight: 700 }}>{m._item}</span> },
                  { key: "_ref", label: "Job / ref", className: "ellipsis", render: (m: any) => <span className="muted">{m._ref || "—"}</span> },
                  { key: "note", label: "Note", className: "ellipsis", render: (m: any) => <span className="muted">{m.note || "—"}</span> },
                  { key: "by_name", label: "By", render: (m: any) => <span className="muted">{m.by_name || "—"}</span> },
                ]} />}
        </div>
      )}

      {tab === "suppliers" && (
        <div className="card pad">
          <div className="row between" style={{ marginBottom: 12 }}>
            <span className="kicker">Suppliers</span>
            <button className="btn btn-outline btn-sm" onClick={async () => {
              const name = prompt("Supplier name"); if (!name) return;
              const ST: any = window.VFP_STOCK;
              try { await ST.saveSupplier({ name }); } catch (e: any) { notify("Couldn't save the supplier — " + String((e && e.message) || e).slice(0, 140)); return; }
              reload();
            }}><Icon name="plus" size={13} />Add supplier</button>
          </div>
          {sups.length === 0
            ? <p className="muted" style={{ fontSize: 13, margin: 0 }}>Add the yards you buy from — POs pick up their order email, and their price sheets import here next (the CSV/XLSX import wizard is the next phase).</p>
            : <DataTable rowKey="id" rows={sups} initialSort={{ key: "name", dir: "asc" }}
                columns={[
                  { key: "name", label: "Supplier", render: (sp: any) => <b style={{ fontSize: 13.5 }}>{sp.name}</b> },
                  { key: "email", label: "Order email", render: (sp: any) => <input className="input dt-input" style={{ minWidth: 180 }} placeholder="orders@…" defaultValue={sp.email || ""} onBlur={e => saveSupplierField({ ...sp, email: e.target.value })} /> },
                  { key: "phone", label: "Phone", render: (sp: any) => <input className="input dt-input" style={{ minWidth: 130 }} placeholder="Phone" defaultValue={sp.phone || ""} onBlur={e => saveSupplierField({ ...sp, phone: e.target.value })} /> },
                  { key: "account_no", label: "Account #", render: (sp: any) => <input className="input dt-input" style={{ minWidth: 110 }} placeholder="Account #" defaultValue={sp.account_no || ""} onBlur={e => saveSupplierField({ ...sp, account_no: e.target.value })} /> },
                  { key: "_act", label: "", sort: false, num: true, render: (sp: any) => <button className="btn btn-ghost btn-sm" style={{ color: "var(--red)" }} title="Remove supplier" onClick={async () => { if (!(await confirmDialog({ title: "Remove " + sp.name + "?", confirmLabel: "Remove supplier", danger: true }))) return; const ST: any = window.VFP_STOCK; try { await ST.deleteSupplier(sp.id); } catch (e: any) { notify("Couldn't remove the supplier — " + String((e && e.message) || e).slice(0, 140)); return; } reload(); }}><Icon name="trash" size={13} /></button> },
                ]} />}
          <p className="faint" style={{ fontSize: 11, margin: "10px 0 0" }}>Different shops get different pricing and most fence suppliers have no API — so costs come from YOUR price sheets: upload, map columns once, re-imports become price-change reports. That import wizard is the next phase; suppliers and item costs entered here already feed the yard value and POs.</p>
        </div>
      )}

      {editItem && <ItemModal initial={editItem} suppliers={sups}
        onSave={async (it: any) => { const ST: any = window.VFP_STOCK; try { await ST.saveItem(it); setEditItem(null); } catch (e: any) { notify("Couldn't save the item — " + String((e && e.message) || e).slice(0, 140)); } reload(); }}
        onDelete={async (id: any) => { const ST: any = window.VFP_STOCK; try { await ST.deleteItem(id); } catch (e) {} setEditItem(null); reload(); }}
        onClose={() => setEditItem(null)} />}
      {adjItem && <AdjustModal item={adjItem} store={store} onDone={() => { setAdjItem(null); reload(); }} onClose={() => setAdjItem(null)} />}
      {poModal && <PurchaseOrderModal initial={poModal} store={store} onClose={() => setPoModal(null)} />}
    </div>
  );
}

Object.assign(window, { InventoryPage, LogMaterialUse });

export { InventoryPage, LogMaterialUse };
